import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserInfo } from '../../helpers/localStorageHandler';
import { Button, Form, Input, Select, Spin, ColorPicker, Popconfirm, Table, Pagination } from 'antd';
import axios from 'axios';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import type { Color } from 'antd/es/color-picker';
import { useRouteContext } from '../../components/CurrentRouteProvider';
import { ValidateStatus } from 'antd/es/form/FormItem';
import { OTPModal } from './OTPModal';
import { useConfigurationContext } from '../../store/configurationContext';
import { StyledPagination } from '../../components/table.style';
import Header from '../../components/Header/Header';
interface Record {
	id: number;
	methodId?: number | null;
	methodName: string;
	color: Color | string;
	bgColor: Color | string;
	public: boolean;
}
interface FieldItem {
	value: string;
	errorMessage: string;
	validationStatus: ValidateStatus;
}
const Country = () => {
	const params = useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [paymentMethods, setPaymentMethods] = useState<Record[]>([]);
	const [countries, setCountries] = useState([]);
	const [showOTPModal, setShowOTPModal] = useState(false);
	const [isPublic, setIsPublic] = useState(false);
	const [page, setPage] = useState(1);
	const [totalElements, setTotalElements] = useState(0);
	const [selectedCountryState, setSelectedCountry] = useState<any>({});

	const [countryName, setCountryName] = useState<FieldItem>({
		value: '',
		validationStatus: 'validating',
		errorMessage: '',
	});
	const [countryCode, setCountryCode] = useState<FieldItem>({
		value: '',
		validationStatus: 'validating',
		errorMessage: '',
	});
	const [currencyCode, setCurrencyCode] = useState<FieldItem>({
		value: '',
		validationStatus: 'validating',
		errorMessage: '',
	});
	const [maxProfitRatio, setMaxProfitRatio] = useState<FieldItem>({
		value: '',
		validationStatus: 'validating',
		errorMessage: '',
	});
	const [exchangeRate, setExchangeRate] = useState<FieldItem>({
		value: '',
		validationStatus: 'validating',
		errorMessage: '',
	});
	const [loading, setLoading] = useState(false);
	const { routeVariable, updateRouteVariable } = useRouteContext();

	useEffect(() => {
		if (params.countryCode) {
			updateRouteVariable('/edit-country');
		} else {
			updateRouteVariable('/add-country');
		}
	}, []);

	useEffect(() => {
		setLoading(true);
		axios
			.get(`${API_ENDPOINTS.getCountriesInfo}`)
			.then((res: any) => {
				setCountries(res.data);
			})
			.finally(() => setLoading(false));
	}, []);
	const submitRecord = (otp: string) => {
		setLoading(true);
		axios(`${API_ENDPOINTS.countryAndPaymMethod}`, {
			method: params.countryCode ? 'PUT' : 'POST',
			data: {
				otp: otp,
				country: {
					countryCode: countryCode.value,
					countryName: countryName.value,
					currencyCode: currencyCode.value,
					exchangeRate: exchangeRate.value ? exchangeRate.value : null,
					maxProfitRatio: `${parseFloat(maxProfitRatio.value) / 100}`,
					isPublic,
				},
				paymentMethod: paymentMethods.map((el) => {
					return {
						countryCode: countryCode.value,
						methodId: el.methodId,
						methodName: el.methodName,
						color: el.color,
						bgColor: el.bgColor,
						isPublic: el.public,
					};
				}),
			},
		})
			.then((_) => navigate('/countries'))
			.catch((err) => console.error(err))
			.finally(() => setLoading(false));
	};
	useEffect(() => {
		if (params.countryCode) {
			setLoading(true);
			axios(`${API_ENDPOINTS.countries}`, {
				method: 'GET',
			})
				.then((res) => {
					const selectedCountry = res.data.find((country: any) => country.countryCode === params.countryCode);
					setCountryName({
						value: selectedCountry.countryName,
						validationStatus: 'success',
						errorMessage: t('requiredField'),
					});
					setCountryCode({
						value: selectedCountry.countryCode,
						validationStatus: 'success',
						errorMessage: t('requiredField'),
					});
					setCurrencyCode({
						errorMessage: t('requiredField'),
						value: selectedCountry.currencyCode,
						validationStatus: 'success',
					});
					setMaxProfitRatio({
						errorMessage: t('requiredField'),
						value: (selectedCountry.maxProfitRatio * 100).toString(),
						validationStatus: 'success',
					});
					setExchangeRate({
						...exchangeRate,
						value: selectedCountry.exchangeRate,
					});
					setIsPublic(selectedCountry.isPublic);
					setTotalElements(selectedCountry.paymentMethods.length);
					setSelectedCountry(selectedCountry);
					const paymentMethods = selectedCountry.paymentMethods
						.slice((page - 1) * 10, page * 10)
						.map((el: any, index: number) => {
							return {
								id: index + 1,
								methodId: el.methodId,
								methodName: el.methodName,
								color: el.color,
								bgColor: el.bgColor,
								public: el.isPublic,
							};
						});
					setPaymentMethods(paymentMethods);
				})
				.catch((err) => console.error(err))
				.finally(() => setLoading(false));
		} else {
			setPaymentMethods([]);
		}
	}, [params.countryCode]);
	const handleDeleteRecord = (id: number) => {
		const updatedData = paymentMethods.filter((row) => row.id !== id);
		setPaymentMethods(updatedData);
	};
	const handleAddRecord = () => {
		setPaymentMethods([
			...paymentMethods,
			{
				id: paymentMethods.length + 1,
				methodId: null,
				methodName: t('paymentMethodName'),
				color: '#a91581',
				bgColor: '#a91581',
				public: true,
			},
		]);
	};
	const handleBackgroundColorChange = (id: number, colorObj: Color) => {
		const updatedData = paymentMethods.map((row) => {
			if (row.id === id) {
				return { ...row, bgColor: colorObj.toHexString() };
			} else {
				return row;
			}
		});
		setPaymentMethods(updatedData);
	};
	const handlePublicChange = (value: string, record: Record) => {
		const updatedData = paymentMethods.map((row) =>
			row.id === record.id ? { ...row, public: value === t('yes') } : row,
		);
		setPaymentMethods(updatedData);
	};
	const handleColorChange = (id: number, colorObj: Color) => {
		const updatedData = paymentMethods.map((row) => (row.id === id ? { ...row, color: colorObj.toHexString() } : row));
		setPaymentMethods(updatedData);
	};

	const setPaymentMethodName = (id: number, newName: string) => {
		const updatedData = paymentMethods.map((row) =>
			row.id === id
				? {
						...row,
						methodName: newName,
				  }
				: row,
		);
		setPaymentMethods(updatedData);
	};

	const paymentMethodsColumns = [
		{
			title: (
				<Button style={{ background: 'none', border: 'none', boxShadow: 'none' }} onClick={handleAddRecord}>
					<PlusOutlined className='flex items-center cursor-pointer  text-[20px] text-black font-bold' />
				</Button>
			),
			dataIndex: 'add',
			key: 'add',

			render: (record: any, rowData: Record) => (
				<div className='w-full' style={{ display: 'flex', justifyContent: 'center' }}>
					{!rowData.methodId ? (
						<Popconfirm
							title={t('confirmNewPaymentMethodDelete')}
							onConfirm={() => handleDeleteRecord(rowData.id)}
							okText={t('yes')}
							cancelText={t('no')}
						>
							<Button type='text' style={{ color: '#0F2147', margin: 'auto' }} icon={<DeleteFilled />} />
						</Popconfirm>
					) : (
						<></>
					)}
				</div>
			),
		},
		{
			title: t('name'),
			dataIndex: 'methodName',
			render: (name: string, record: Record) => (
				<Input value={name} onChange={(e) => setPaymentMethodName(record.id, e.target.value)} />
			),
		},
		{
			title: t('color'),
			dataIndex: 'color',
			render: (rowValue: string, record: Record) => (
				<ColorPicker format='hex' value={record.color} onChange={(e) => handleColorChange(record.id, e)} />
			),
		},
		{
			title: t('backgroundColor'),
			dataIndex: 'bgColor',
			render: (rowValue: string, record: Record) => (
				<ColorPicker format='hex' value={record.bgColor} onChange={(e) => handleBackgroundColorChange(record.id, e)} />
			),
		},
		{
			title: t('public'),
			dataIndex: 'public',
			render: (value: any, record: Record) => (
				<Select
					defaultValue={value ? t('yes') : t('no')}
					style={{ width: 80 }}
					onChange={(val) => handlePublicChange(val, record)}
				>
					<Select.Option value={t('yes')}>{t('yes')}</Select.Option>
					<Select.Option value={t('no')}>{t('no')}</Select.Option>
				</Select>
			),
		},
	];
	const getErrorMessage = (value: string) => {
		if (!value) {
			return t('requiredField');
		} else if (+value < 0) {
			return t('biggerThanZero');
		} else if (+value > 100) {
			return t('lessThanhundred');
		} else {
			return '';
		}
	};
	const onShowOTPModal = () => {
		if (!countryCode.value) {
			setCountryCode({ ...countryCode, validationStatus: 'error', errorMessage: t('requiredField') });
		}
		if (!maxProfitRatio.value) {
			setMaxProfitRatio({ ...maxProfitRatio, validationStatus: 'error', errorMessage: t('requiredField') });
		}
		if (!countryName.value) {
			setCountryName({ ...countryName, validationStatus: 'error', errorMessage: t('requiredField') });
		}

		if (countryName.value && maxProfitRatio.value && countryCode.value) setShowOTPModal(true);
	};

	const { configurationState } = useConfigurationContext();
	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondary_color = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';

	useEffect(() => {
		if (countryName && countries?.length > 0 && !params.countryCode) {
			let country: any = countries.find((country: any) => country.name === countryName.value);
			if (country) {
				setCountryCode({
					value: country.countryCode,
					validationStatus: 'success',
					errorMessage: '',
				});
				setCurrencyCode({
					value: country.currencyCode,
					validationStatus: 'success',
					errorMessage: '',
				});
			}
		}
	}, [countryName, params.countryCode]);
	const isValid = useMemo(() => {
		if (!countryName.value || !countryCode.value || !currencyCode.value || !maxProfitRatio.value) return false;
		if (
			countryName.value == selectedCountryState.countryName &&
			countryCode.value == selectedCountryState.countryCode &&
			currencyCode.value == selectedCountryState.currencyCode &&
			maxProfitRatio.value == (selectedCountryState.maxProfitRatio * 100).toString() &&
			isPublic == selectedCountryState.isPublic &&
			exchangeRate.value == selectedCountryState.exchangeRate &&
			!hasArrayChanges(selectedCountryState.paymentMethods, paymentMethods)
		)
			return false;
		return true;
	}, [
		countryName,
		countryCode,
		currencyCode,
		maxProfitRatio,
		paymentMethods,
		isPublic,
		selectedCountryState,
		exchangeRate,
	]);
	return (
		<>
			<div>
				<Header title={t('Country/Payment Methods')} description={t<string>('CountryAndPaymentMethods')} />
				<div className='border border-solid border-[#E6E6E6] rounded-md p-4 max-w-[900px] mx-auto lg:px-[75px] pt-[20px] pb-20 my-10 bg-white'>
					<Spin spinning={loading}>
						<Form layout='vertical'>
							<div
								className='flex justify-between'
								style={{
									flexWrap: 'wrap',
									minWidth: '85%',
									margin: 'auto',
								}}
							>
								<h2
									className='w-full text-[#00AFFF]'
									style={{
										borderBottomStyle: 'solid',
										borderBottomColor: '#C2C2C2',
										borderBottomWidth: '0.05rem',
									}}
								>
									{t('country')}
								</h2>
								<div className='xss-[100%] lg:w-[40%]'>
									<div className='flex flex-wrap'>
										<div className='w-full'>
											<Form.Item
												required
												validateStatus={countryName.validationStatus}
												help={countryName.validationStatus !== 'success' ? countryName.errorMessage : null}
												initialValue={countryName}
												valuePropName='countryName'
												name='countryName'
												label={<b>{t('name')}</b>}
												className='w-[300px]'
											>
												<Select
													disabled={params.countryCode ? true : false}
													placeholder={t('Select Country')}
													value={countryName.value || undefined}
													onChange={(e: string) => {
														setCountryName({
															value: e,
															validationStatus: e ? 'success' : 'error',
															errorMessage: e ? '' : t('requiredField'),
														});
													}}
													showSearch
													filterOption={(inputValue, option: any) =>
														option?.children
															? option.children[1].toLowerCase().includes(inputValue.toLowerCase())
															: false
													}
												>
													{countries?.map((item: any, index) => (
														<Select.Option key={index} value={item.name}>
															<img
																src={`https://flagsapi.com/${item.countryCode}/shiny/64.png`}
																alt='country'
																className='country-flag'
															/>
															{item.name}
														</Select.Option>
													))}
												</Select>
											</Form.Item>
										</div>
										<div className='w-full'>
											<Form.Item
												validateStatus={currencyCode.validationStatus}
												help={currencyCode.validationStatus !== 'success' ? currencyCode.errorMessage : null}
												initialValue={currencyCode}
												valuePropName='currencyCode'
												name='currencyCode'
												label={<b>{t('currencyCode')}</b>}
												required
												className='w-[300px]'
											>
												<Select
													placeholder={t('currencyCode')}
													value={currencyCode.value}
													onChange={(e: string) => {
														setCurrencyCode({
															value: e,
															validationStatus: e ? 'success' : 'error',
															errorMessage: e ? '' : t('requiredField'),
														});
													}}
													showSearch
													filterOption={(inputValue, option: any) =>
														option?.children ? option.children.toLowerCase().includes(inputValue.toLowerCase()) : false
													}
												>
													{['USDT', ...(new Set(countries.map((c: any) => c.currencyCode)) as any)].map(
														(item: any, index) => (
															<Select.Option key={index} value={item}>
																{item}
															</Select.Option>
														),
													)}
												</Select>
											</Form.Item>
										</div>
										<div className='w-full'>
											<Form.Item
												validateStatus={maxProfitRatio.validationStatus}
												help={maxProfitRatio.validationStatus !== 'success' ? maxProfitRatio.errorMessage : null}
												initialValue={maxProfitRatio}
												valuePropName='maxProfitRatio'
												name='maxProfitRatio'
												label={<b>{t('maxProfitRatio')}</b>}
												required
												className='w-[300px]'
											>
												<Input
													value={maxProfitRatio.value}
													onChange={(e) => {
														setMaxProfitRatio({
															validationStatus: !getErrorMessage(e.target.value) ? 'success' : 'error',
															errorMessage: getErrorMessage(e.target.value),
															value: e.target.value,
														});
													}}
													min={1}
													max={100}
													type='number'
													suffix='%'
													onWheel={(e) => (e.target as HTMLInputElement).blur()}
												/>
											</Form.Item>
										</div>
									</div>
								</div>
								<div style={{ width: '40%' }}>
									<div className='flex flex-wrap'>
										<div className='w-full'>
											<Form.Item
												required
												validateStatus={countryCode.validationStatus}
												help={countryCode.validationStatus !== 'success' ? countryCode.errorMessage : null}
												valuePropName='countryCode'
												name='countryCode'
												label={<b>{t('countryCode')}</b>}
												className='w-[300px]'
											>
												<Input
													disabled
													value={countryCode.value}
													onChange={(e) => {
														setCountryCode({
															errorMessage: e.target.value ? '' : t('requiredField'),
															value: e.target.value,
															validationStatus: e.target.value ? 'success' : 'error',
														});
													}}
												/>
											</Form.Item>
										</div>
										<div className='w-full'>
											<Form.Item
												required
												name='isPublic'
												valuePropName='isPublic'
												label={<b>{t('public')}</b>}
												className='w-[300px]'
											>
												<Select
													defaultValue={isPublic === true ? t('yes') : t('no')}
													value={isPublic === true ? t('yes') : t('no')}
													loading={false}
													onChange={(e: string) => {
														setIsPublic(e === t('yes') ? true : false);
													}}
												>
													{[
														{ label: t('yes'), value: t('yes') },
														{ label: t('no'), value: t('no') },
													].map((item, index) => (
														<Select.Option key={index} value={item.value}>
															{item.label}
														</Select.Option>
													))}
												</Select>
											</Form.Item>
										</div>
										<div className='w-full'>
											<Form.Item
												valuePropName='exchangeRate'
												name='exchangeRate'
												label={<b>{t('exchangeRate')}</b>}
												className='w-[300px]'
											>
												<Input
													type='number'
													value={exchangeRate.value}
													onChange={(e) => {
														setExchangeRate({
															...exchangeRate,
															value: e.target.value,
														});
													}}
													min={1}
													onWheel={(e) => (e.target as HTMLInputElement).blur()}
												/>
											</Form.Item>
										</div>
									</div>
								</div>
								<h2
									className='w-full text-[#00AFFF]'
									style={{
										borderBottomStyle: 'solid',
										borderBottomColor: '#C2C2C2',
										borderBottomWidth: '0.05rem',
									}}
								>
									{t('paymentMethods')}
								</h2>
								<div className='w-full'>
									<div>
										<StyledPagination bgColor={themeColor} color={secondary_color}>
											<Table
												pagination={false}
												columns={paymentMethodsColumns}
												dataSource={paymentMethods}
												rowKey='id'
												tableLayout='fixed'
											/>
											<div className='m-4 flex justify-end'>
												<Pagination
													showSizeChanger={false}
													pageSize={10}
													current={page}
													total={totalElements}
													onChange={(pageIndex) => setPage(pageIndex)}
												/>
											</div>
										</StyledPagination>
									</div>
								</div>
								<div className='w-full pt-3 flex justify-end'>
									<Button
										className='px-10 bg-[#01A2FF] font-bold'
										style={{ boxShadow: 'none' }}
										onClick={() => onShowOTPModal()}
										type='primary'
										disabled={
											countryCode.validationStatus === 'error' ||
											maxProfitRatio.validationStatus === 'error' ||
											currencyCode.validationStatus === 'error' ||
											countryName.validationStatus === 'error' ||
											!isValid
										}
									>
										{t<string>('submit')}
									</Button>
								</div>
							</div>
						</Form>
					</Spin>
				</div>
			</div>
			<OTPModal
				showOTPModal={showOTPModal}
				setOTPModal={setShowOTPModal}
				handelAccept={submitRecord}
				code={countryCode.value}
				getOtpUrl={API_ENDPOINTS.getOtp}
				validateOtpUrl={API_ENDPOINTS.validateOtp}
				accountGroup={''}
			/>
		</>
	);
};

export default Country;

function hasArrayChanges(arr1: any[], arr2: any[]) {
	// Check if the arrays are of different lengths
	if (arr1?.length !== arr2?.length) {
		return true;
	}

	// Compare each object in the arrays
	for (let i = 0; i < arr1.length; i++) {
		const obj1 = arr1[i];
		const obj2 = arr2[i];

		// Check if the objects have different properties
		if (
			obj1.methodName !== obj2.methodName ||
			obj1.color !== obj2.color ||
			obj1.bgColor !== obj2.bgColor ||
			obj1.isPublic !== obj2.public // Include public property in the comparison
		) {
			return true; // Change detected
		}
	}

	// No changes detected
	return false;
}
